import { TemplateSpecialization, TemplateAccountingCategory, PayBy } from '@/utils/Enumerations.js'
import i18n from '@/i18n'

function aggregateFromNormalizedEntries (entries) {
  const flat = entries.flatMap(({ counterpartyName, loadSummary, contractInfo }) => {
    const ticket = { ticketId: loadSummary.ticketId, postBatchId: loadSummary.postBatchId }
    return loadSummary.paymentDetails
      .filter(pd => pd.activityTemplateSpecialization !== TemplateSpecialization.Penalty.value && pd.computedAtPayBy !== PayBy.Volume.value)
      .map(pd => ({
        activity: pd.activity,
        product: loadSummary.product,
        rate: pd.computedAtRate,
        counterparty: counterpartyName,
        contractInfo,
        ticket
      }))
  })

  const grouped = Object.groupBy(flat, item =>
    [item.activity, item.product, item.rate, item.counterparty].join(',')
  )

  return Object.values(grouped).map((group, _key) => {
    const { activity, product, rate, counterparty } = group[0]
    const contractGroups = Object.groupBy(group, item => item.contractInfo.contractId)
    const contracts = Object.values(contractGroups).map(items => ({
      ...items[0].contractInfo,
      tickets: items.map(({ ticket }) => ticket)
    }))
    return { activity, product, rate, counterparty, contracts, _key }
  })
}

export function aggregateSettlementsByContract (settlements) {
  if (!settlements?.length) return []
  const entries = settlements.flatMap(s =>
    s.payeeInfo.flatMap(p =>
      p.loadSummaries.map(ls => ({
        counterpartyName: p.payeeName,
        loadSummary: ls,
        contractInfo: { ...s.contractInfo }
      }))
    )
  )
  return aggregateFromNormalizedEntries(entries)
}

export function aggregateSettlementsByPayee (payees) {
  if (!payees?.length) return []
  const entries = payees.flatMap(p =>
    p.contracts.flatMap(c =>
      c.loadSummaries.map(ls => ({
        counterpartyName: p.payeeName,
        loadSummary: ls,
        contractInfo: { contractId: c.contractId, accountName: c.accountName, destinationName: c.destinationName, settingName: c.settingName, tractName: c.tractName, type: c.type }
      }))
    )
  )
  return aggregateFromNormalizedEntries(entries)
}

export function aggregateRatesForTractContracts (contracts) {
  if (!contracts || contracts.length === 0) return []

  const flat = contracts.flatMap(contract =>
    contract.activities
      .filter(a => (a.activityTemplateSpecialization !== TemplateSpecialization.Penalty.value || a.activityTemplateCategory === TemplateAccountingCategory.AccountsReceivable.value) && a.payBy !== PayBy.Volume.value)
      .flatMap(activity => {
        const isRcp = activity.contractorName == null && activity.runtimeCounterparty != null
        const counterparty = isRcp ? activity.runtimeCounterparty : activity.contractorName
        const details = activity.activityDetails.map(detail => ({
          activity: activity.activityTemplateName,
          rate: detail.rate,
          counterparty,
          isRcp,
          product: detail.productName,
          rateKey: [activity.activityTemplateName, detail.rate, counterparty, isRcp, detail.productName].join(','),
          contractEntry: {
            contractId: contract.contractId,
            accountName: contract.accountName,
            destinationName: contract.destinationName,
            settingName: contract.settingName,
            tractName: contract.tractName,
            type: contract.type
          }
        }))
        // Add base rate row if present
        if (activity.baseRate != null) {
          details.push({
            activity: activity.activityTemplateName,
            rate: activity.baseRate,
            counterparty,
            isRcp,
            product: i18n.t('baseRate'),
            rateKey: [activity.activityTemplateName, activity.baseRate, counterparty, isRcp, i18n.t('baseRate')].join(','),
            contractEntry: {
              contractId: contract.contractId,
              accountName: contract.accountName,
              destinationName: contract.destinationName,
              settingName: contract.settingName,
              tractName: contract.tractName,
              type: contract.type
            }
          })
        }
        return details
      })
  )

  const grouped = Object.groupBy(flat, item => item.rateKey)

  return Object.values(grouped).map((group, _key) => {
    const { activity, rate, counterparty, isRcp, product } = group[0]
    const contracts = group
      .map(({ contractEntry }) => contractEntry)
      .filter((c, i, arr) => arr.findIndex(x => x.contractId === c.contractId) === i)
    return { activity, rate, counterparty, isRcp, product, contracts, _key }
  })
}
